import React, { useEffect, useId, useMemo, useRef } from 'react';
import { FiAlertTriangle, FiArrowLeft, FiCheckCircle, FiLoader, FiX } from 'react-icons/fi';
import { useDialogFocus } from '../../../hooks/useDialogFocus';
import { useI18n } from '../../../i18n';
import type { RosterRuleViolation, SiteRow } from '../../../services/dutyBoardService';
import {
  createRemediationEngine,
  selectedFixes,
  type RemediationEngine,
  type RosterSite,
} from '../../../services/rosterCompliance';
import { rosterComplianceService } from '../../../services/rosterComplianceService';
import { formatDateKeyLabel, resolveSiteTimeZone } from '../../../utils/siteScheduling';
import ChangeReview from './ChangeReview';
import { issueLabel } from './fixPresentation';
import IssueFixCard from './IssueFixCard';
import { useComplianceFixes } from './useComplianceFixes';
import './ComplianceFixModal.css';

const defaultEngine = createRemediationEngine({ gateway: rosterComplianceService });

export interface ComplianceFixModalProps {
  site: Pick<SiteRow, 'id' | 'name' | 'country_code' | 'timezone'>;
  dateKey: string;
  onClose: () => void;
  /** Changes were written; the page should reload this roster. */
  onApplied: (dateKey: string, remaining: RosterRuleViolation[]) => void;
  /** Publish the day. Offered once no breach remains. */
  onPublish: (dateKey: string) => void;
  /** Swappable for tests; defaults to the Supabase-backed engine. */
  engine?: RemediationEngine;
}

/**
 * Suggested fixes for a roster's rule breaches. Nothing is written until the
 * admin has seen the exact changes on the review step and confirmed them.
 */
export default function ComplianceFixModal({
  site,
  dateKey,
  onClose,
  onApplied,
  onPublish,
  engine = defaultEngine,
}: ComplianceFixModalProps) {
  const { t, tn } = useI18n();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Keyed on the fields, not the object: a parent re-render must not restart the flow.
  const { id: siteId, name: siteName, country_code: siteCountryCode, timezone: siteTimezone } = site;
  const rosterSite = useMemo<RosterSite>(() => ({
    id: siteId,
    name: siteName,
    countryCode: siteCountryCode?.trim().toUpperCase() ?? '',
    timezone: resolveSiteTimeZone({ country_code: siteCountryCode, timezone: siteTimezone }),
  }), [siteCountryCode, siteId, siteName, siteTimezone]);

  const flow = useComplianceFixes(engine, rosterSite, dateKey);
  const { step, plan, issues, selection, operations } = flow;
  const isApplying = step === 'applying';
  const isReviewing = step === 'review' || isApplying;

  useDialogFocus(dialogRef);

  // Each step starts at its top; otherwise review opens scrolled to wherever the choices were left.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [step]);

  const requestClose = () => {
    if (!isApplying) onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      requestClose();
    }
  };

  const handleApply = async () => {
    const response = await flow.apply();
    if (response) onApplied(dateKey, response.violations);
  };

  const fixes = useMemo(() => selectedFixes(issues, selection), [issues, selection]);
  const fixableCount = issues.filter((issue) => issue.options.length > 0).length;
  const remaining = flow.result?.violations ?? [];
  const reviewBlocked = flow.review.status !== 'ready' || flow.review.verification?.status === 'failed';

  const issueLabelByKey = useMemo(
    () => new Map(issues.map((issue) => [issue.key, issueLabel(issue, { t, tn })])),
    [issues, t, tn],
  );

  const stepNumber = step === 'choose' || step === 'loading' || step === 'error' ? 1 : 2;

  return (
    <div
      className="cfx-overlay"
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) requestClose(); }}
    >
      <div
        ref={dialogRef}
        className="cfx-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <header className="cfx-header">
          <div className="cfx-header__text">
            <p className="cfx-eyebrow">{t('Roster rules')}</p>
            <h2 id={titleId} className="cfx-title">{t('Fix roster breaches')}</h2>
            <p className="cfx-subtitle">{site.name} · {formatDateKeyLabel(dateKey)}</p>
          </div>
          {step !== 'done' && (
            <ol className="cfx-steps" aria-label={t('Steps')}>
              <li className={`cfx-step${stepNumber === 1 ? ' is-current' : ' is-complete'}`} aria-current={stepNumber === 1 ? 'step' : undefined}>
                <span className="cfx-step__number" aria-hidden="true">1</span>
                {t('Choose fixes')}
              </li>
              <li className={`cfx-step${stepNumber === 2 ? ' is-current' : ''}`} aria-current={stepNumber === 2 ? 'step' : undefined}>
                <span className="cfx-step__number" aria-hidden="true">2</span>
                {t('Review and confirm')}
              </li>
            </ol>
          )}
          <button type="button" className="cfx-close" onClick={requestClose} disabled={isApplying} aria-label={t('Close')}>
            <FiX aria-hidden="true" />
          </button>
        </header>

        <div ref={bodyRef} className="cfx-body" aria-busy={step === 'loading' || isApplying}>
          {step === 'loading' && (
            <div className="cfx-state" role="status">
              <FiLoader className="cfx-spin" aria-hidden="true" />
              <p className="cfx-state__title">{t('Working out fixes…')}</p>
              <p className="cfx-state__detail">{t('Each suggestion is checked against the roster rules before you see it.')}</p>
            </div>
          )}

          {step === 'error' && (
            <div className="cfx-state cfx-state--error" role="alert">
              <FiAlertTriangle aria-hidden="true" />
              <p className="cfx-state__title">{t('Fix suggestions could not be loaded')}</p>
              <p className="cfx-state__detail">{flow.error}</p>
            </div>
          )}

          {step === 'choose' && plan && issues.length === 0 && (
            <div className="cfx-state cfx-state--success" role="status">
              <FiCheckCircle aria-hidden="true" />
              <p className="cfx-state__title">{t('This roster is within the rules')}</p>
              <p className="cfx-state__detail">{t('There is nothing to fix. You can publish the day.')}</p>
            </div>
          )}

          {step === 'choose' && plan && issues.length > 0 && (
            <>
              <div className="cfx-intro">
                <p className="cfx-intro__text">
                  {tn('{count} breach on this roster.', '{count} breaches on this roster.', issues.length)}
                  {' '}
                  {t('Pick a fix for each, then review the changes before anything is saved.')}
                </p>
                {fixableCount > 0 && (
                  <button type="button" className="cfx-link" onClick={flow.resetToRecommended}>
                    {t('Use recommended fixes')}
                  </button>
                )}
              </div>

              {flow.clearedIssueKeys.length > 0 && (
                <p className="cfx-notice" role="status">
                  <FiAlertTriangle aria-hidden="true" />
                  {t('Your fix for {issues} was set aside because it changes the same guard’s day. Apply this one first, then fix what remains.', {
                    issues: flow.clearedIssueKeys.map((key) => issueLabelByKey.get(key) ?? '').join(', '),
                  })}
                </p>
              )}

              <ul className="cfx-issues">
                {issues.map((issue) => {
                  const coveredBy = flow.covered.get(issue.key);
                  return (
                    <li key={issue.key}>
                      <IssueFixCard
                        issue={issue}
                        selectedOptionId={selection[issue.key] ?? null}
                        coveredByLabel={coveredBy ? issueLabelByKey.get(coveredBy) ?? null : null}
                        onChoose={(optionId) => flow.choose(issue.key, optionId)}
                      />
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {isReviewing && plan && flow.projected && (
            <ChangeReview
              plan={plan}
              fixes={fixes}
              operations={operations}
              projected={flow.projected}
              review={flow.review}
              applyError={flow.error}
              guardName={flow.guardName}
            />
          )}

          {step === 'done' && flow.result && (
            remaining.length === 0 ? (
              <div className="cfx-state cfx-state--success" role="status">
                <FiCheckCircle aria-hidden="true" />
                <p className="cfx-state__title">{t('The roster is now within the rules')}</p>
                <p className="cfx-state__detail">
                  {tn('{count} change was applied.', '{count} changes were applied.', flow.result.appliedOperations)}
                  {' '}
                  {t('The changed duties are drafts until you publish the day.')}
                </p>
              </div>
            ) : (
              <div className="cfx-state cfx-state--warning" role="status">
                <FiAlertTriangle aria-hidden="true" />
                <p className="cfx-state__title">
                  {tn('Changes applied. {count} breach remains.', 'Changes applied. {count} breaches remain.', remaining.length)}
                </p>
                <ul className="cfx-state__list">
                  {remaining.map((violation, index) => (
                    <li key={`${violation.rule_code}-${violation.guard_id}-${index}`}>{violation.message}</li>
                  ))}
                </ul>
              </div>
            )
          )}
        </div>

        <footer className="cfx-footer">
          {step === 'choose' && issues.length > 0 && (
            <p className="cfx-footer__status">
              {tn('{count} fix selected', '{count} fixes selected', fixes.length)}
            </p>
          )}

          <div className="cfx-footer__actions">
            {(step === 'loading' || step === 'error') && (
              <button type="button" className="btn btn--secondary" onClick={requestClose}>{t('Cancel')}</button>
            )}
            {step === 'error' && (
              <button type="button" className="btn btn--primary" onClick={() => { void flow.reload(); }}>{t('Try again')}</button>
            )}

            {step === 'choose' && issues.length === 0 && (
              <>
                <button type="button" className="btn btn--secondary" onClick={requestClose}>{t('Close')}</button>
                <button type="button" className="btn btn--primary" onClick={() => onPublish(dateKey)}>{t('Publish roster')}</button>
              </>
            )}

            {step === 'choose' && issues.length > 0 && (
              <>
                <button type="button" className="btn btn--secondary" onClick={requestClose}>{t('Cancel')}</button>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => { void flow.startReview(); }}
                  disabled={operations.length === 0}
                >
                  {t('Review changes')}
                </button>
              </>
            )}

            {isReviewing && (
              <>
                <button type="button" className="btn btn--secondary" onClick={flow.backToChoices} disabled={isApplying}>
                  <FiArrowLeft aria-hidden="true" />
                  {t('Back')}
                </button>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => { void handleApply(); }}
                  disabled={isApplying || reviewBlocked}
                >
                  {isApplying
                    ? t('Applying…')
                    : tn('Confirm and apply {count} change', 'Confirm and apply {count} changes', operations.length)}
                </button>
              </>
            )}

            {step === 'done' && (
              <>
                <button type="button" className="btn btn--secondary" onClick={requestClose}>{t('Close')}</button>
                {remaining.length === 0 ? (
                  <button type="button" className="btn btn--primary" onClick={() => onPublish(dateKey)}>{t('Publish roster')}</button>
                ) : (
                  <button type="button" className="btn btn--primary" onClick={() => { void flow.reload(); }}>{t('Fix remaining breaches')}</button>
                )}
              </>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
